import React, { useContext, useEffect, useState } from "react";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Grid,
  Button,
  IconButton,
  LinearProgress,
} from "@material-ui/core";
import { Close, Refresh } from "@material-ui/icons";
import api from "../../services/api";
import { socketConnection } from "../../services/socket";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

// Mesmo limite de MAX_QRCODE_RETRIES no backend (libs/wbot.ts).
const MAX_QR_CODES = 3;
// O WhatsApp troca o primeiro código em ~60 s e os seguintes em ~20 s.
const qrLifetimeMs = codeNumber => (codeNumber <= 1 ? 60000 : 20000);

const useStyles = makeStyles(theme => ({
  "@keyframes cornerBlink": {
    "0%, 100%": { opacity: 1 },
    "50%":      { opacity: 0.2 },
  },
  "@keyframes dots": {
    "0%, 80%, 100%": { opacity: 0 },
    "40%":           { opacity: 1 },
  },

  head: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2.75, 3, 1),
  },
  headTitle: { fontSize: 20, fontWeight: 600, lineHeight: 1.3 },
  headSub: { fontSize: 13, color: theme.palette.text.secondary, marginTop: 2 },
  help: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    backgroundColor: theme.palette.action.hover,
    fontSize: 13,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    "& b": { color: theme.palette.text.primary },
  },
  timer: { width: 264, marginTop: theme.spacing(1.5) },
  timerRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 6,
    "& b": { color: theme.palette.text.primary },
  },
  timerBar: { height: 6, borderRadius: 999 },
  lastCode: {
    width: 264,
    fontSize: 12,
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginTop: 6,
  },
  newCodeBtn: {
    marginTop: theme.spacing(1.5),
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
  },
  footer: {
    padding: theme.spacing(1.5, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  footerHint: { fontSize: 12, color: theme.palette.text.secondary, flex: "1 1 260px", "& b": { color: theme.palette.text.primary } },

  content: {
    padding: theme.spacing(2, 3, 3),
  },

  /* ---------- instructions ---------- */
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
  },
  stepLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: theme.palette.type === "dark" ? "rgba(144,202,249,0.16)" : "rgba(45,123,232,0.12)",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  stepTitle: {
    fontWeight: 600,
    fontSize: "0.94rem",
    color: theme.palette.text.primary,
    lineHeight: 1.35,
  },
  stepDesc: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.4,
  },

  /* ---------- QR panel ---------- */
  qrPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
      paddingLeft: 0,
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },

  /* frame ao redor do QR */
  qrFrame: {
    position: "relative",
    // Fundo sempre branco, independente do tema: o QR Code precisa de uma
    // "quiet zone" clara ao redor para os leitores (câmera do celular)
    // conseguirem localizar o padrão. No tema dark, o fundo escuro do
    // modal encostado direto no QR atrapalhava a leitura.
    padding: 16,
    borderRadius: 10,
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    lineHeight: 0,
  },

  /* placeholder enquanto carrega */
  placeholder: {
    width: 256,
    height: 256,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.03)",
  },
  corner: {
    position: "absolute",
    width: 28,
    height: 28,
    animation: "$cornerBlink 1.6s ease-in-out infinite",
  },
  cTL: {
    top: 12, left: 12,
    borderTop:  `3px solid ${theme.palette.primary.main}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    borderRadius: "3px 0 0 0",
    animationDelay: "0s",
  },
  cTR: {
    top: 12, right: 12,
    borderTop:   `3px solid ${theme.palette.primary.main}`,
    borderRight: `3px solid ${theme.palette.primary.main}`,
    borderRadius: "0 3px 0 0",
    animationDelay: "0.4s",
  },
  cBL: {
    bottom: 12, left: 12,
    borderBottom: `3px solid ${theme.palette.primary.main}`,
    borderLeft:   `3px solid ${theme.palette.primary.main}`,
    borderRadius: "0 0 0 3px",
    animationDelay: "0.8s",
  },
  cBR: {
    bottom: 12, right: 12,
    borderBottom: `3px solid ${theme.palette.primary.main}`,
    borderRight:  `3px solid ${theme.palette.primary.main}`,
    borderRadius: "0 0 3px 0",
    animationDelay: "1.2s",
  },

  placeholderText: {
    marginTop: theme.spacing(1.5),
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  dotsWrap: {
    display: "inline-flex",
    gap: 3,
    "& span": {
      width: 4,
      height: 4,
      borderRadius: "50%",
      backgroundColor: theme.palette.text.secondary,
      animation: "$dots 1.4s ease-in-out infinite",
      "&:nth-child(2)": { animationDelay: "0.2s" },
      "&:nth-child(3)": { animationDelay: "0.4s" },
    },
  },

  qrHint: {
    marginTop: theme.spacing(1),
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const Step = ({ number, title, desc, classes }) => (
  <div className={classes.step}>
    <div className={classes.stepLeft}>
      <div className={classes.stepNumber}>{number}</div>
    </div>
    <div>
      <Typography className={classes.stepTitle}>{title}</Typography>
      <Typography className={classes.stepDesc}>{desc}</Typography>
    </div>
  </div>
);

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [qrCode, setQrCode] = useState("");
  const [qrReceivedAt, setQrReceivedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [requesting, setRequesting] = useState(false);

  // Nome e contador de códigos vêm da lista, que o socket mantém atualizada.
  const whatsApp = whatsApps.find(w => w.id === whatsAppId);
  const codeNumber = Math.min(whatsApp?.retries || 1, MAX_QR_CODES);

  useEffect(() => {
    setQrReceivedAt(qrCode ? Date.now() : null);
  }, [qrCode]);

  // Relógio da contagem regressiva, só enquanto há um código na tela.
  useEffect(() => {
    if (!open || !qrCode) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [open, qrCode]);

  useEffect(() => {
    if (!open) { setQrCode(""); return; }
    const fetchSession = async () => {
      if (!whatsAppId) return;
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId, open]);

  useEffect(() => {
    if (!open || !whatsAppId) return;
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-whatsappSession`, data => {
      // Antes qualquer conexão sem QR fechava o modal, inclusive o próprio
      // "OPENING" de uma conexão recém-criada, antes de o QR ser gerado.
      if (data.action !== "update" || data.session.id !== whatsAppId) return;
      const { status, qrcode } = data.session;

      if (qrcode) {
        setQrCode(qrcode);
        return;
      }
      // Iniciando ou reconectando logo após a leitura: aguarda o desfecho.
      if (status === "OPENING" || status === "PENDING") {
        setQrCode("");
        return;
      }
      if (status === "CONNECTED") {
        toast.success(
          "WhatsApp conectado! Para testar, mande um \"oi\" de outro celular para este número e acompanhe em Atendimentos.",
          { autoClose: 10000 }
        );
      }
      if (status === "DISCONNECTED") {
        toast.info("O QR code expirou sem leitura. Clique em Reconectar para gerar outro quando quiser.");
      }
      onClose();
    });

    return () => socket.disconnect();
  }, [open, whatsAppId, onClose]);

  const handleNewCode = async () => {
    if (!whatsAppId) return;
    setRequesting(true);
    setQrCode("");
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
    setRequesting(false);
  };

  const lifetime = qrLifetimeMs(codeNumber);
  const elapsed = qrReceivedAt ? now - qrReceivedAt : 0;
  const remainingSec = Math.max(0, Math.ceil((lifetime - elapsed) / 1000));
  const progress = qrReceivedAt ? Math.min(100, (elapsed / lifetime) * 100) : 0;
  const isLastCode = codeNumber >= MAX_QR_CODES;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <div className={classes.head}>
        <div>
          <Typography component="h2" className={classes.headTitle}>Conectar o WhatsApp</Typography>
          {whatsApp?.name && (
            <Typography className={classes.headSub}>Conexão: {whatsApp.name}</Typography>
          )}
        </div>
        <IconButton aria-label="Fechar" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        <Grid container>
          {/* Instruções */}
          <Grid item xs={12} sm={6}>
            <Step
              number="1"
              title="Abra o WhatsApp no celular"
              desc="Use o celular do número que você quer conectar. WhatsApp Business também funciona."
              classes={classes}
            />
            <Step
              number="2"
              title="Toque em Mais opções ⋮ ou Configurações"
              desc="Depois em Dispositivos conectados › Conectar dispositivo."
              classes={classes}
            />
            <Step
              number="3"
              title="Aponte a câmera para o código ao lado"
              desc="Esta janela fecha sozinha quando a conexão for confirmada."
              classes={classes}
            />
            <div className={classes.help}>
              <b>Não está conseguindo?</b> Se o celular disser que o código é inválido,
              gere um novo. O celular precisa estar com internet durante a leitura.
            </div>
          </Grid>

          {/* QR Code */}
          <Grid item xs={12} sm={6} className={classes.qrPanel}>
            {qrCode ? (
              <>
                <div className={classes.qrFrame}>
                  <QRCode value={qrCode} size={232} level="H" includeMargin />
                </div>
                <div className={classes.timer}>
                  <div className={classes.timerRow}>
                    <span>
                      {remainingSec > 0 ? (
                        <>Código renova em <b>~{remainingSec} s</b></>
                      ) : (
                        "Aguardando o próximo código…"
                      )}
                    </span>
                    <span>Código {codeNumber} de {MAX_QR_CODES}</span>
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    className={classes.timerBar}
                  />
                </div>
                {isLastCode && (
                  <Typography className={classes.lastCode}>
                    Último código: se expirar, a conexão desliga e você pode tentar de novo.
                  </Typography>
                )}
              </>
            ) : (
              <>
                <div className={classes.placeholder}>
                  <div className={`${classes.corner} ${classes.cTL}`} />
                  <div className={`${classes.corner} ${classes.cTR}`} />
                  <div className={`${classes.corner} ${classes.cBL}`} />
                  <div className={`${classes.corner} ${classes.cBR}`} />
                  <CircularProgress size={44} />
                </div>
                <div className={classes.placeholderText}>
                  Gerando QR code
                  <span className={classes.dotsWrap}>
                    <span /><span /><span />
                  </span>
                </div>
              </>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              className={classes.newCodeBtn}
              onClick={handleNewCode}
              disabled={requesting || !qrCode}
            >
              Gerar novo código
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.footer}>
        <Typography className={classes.footerHint}>
          Pode fechar esta janela: a conexão continua esperando a leitura e você volta por <b>Mostrar QR code</b>.
        </Typography>
        <Button variant="outlined" onClick={onClose} style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
